// @ts-strict-ignore
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { timer } from 'rxjs';
import { GlobalIcon, GlobalIconSize } from '../../../components/icon/icon.constants';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ReadOnlyDirective } from '../../../directives/read-only.directive';

/**
 * By default the button type is set as `primary` for the `editor`. To style it differently you can make use
 * of the directives `dashboard`, `secondary`, `neutral`, `danger`, `small` and `borderless`,
 * which map to the types in the figma.
 *
 * - `primary` and `secondary` are mutually exclusive.
 * - `editor` and `dashboard` are mutually exclusive.
 * - `danger` and `neutral` are mutually excluse, and can only be used with `editor`.
 * - `small` and `borderless` can be used together with any other directive.
 *
 * You can also conditionally render a specific style, e.g.:
 * ```html
 * <app-button secondary [small]="isSmall">
 *  Secondary editor button that conditionally is small
 * </app-button>
 * ```
 */
@Component({
    selector: 'app-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    imports: [CommonModule, AngularSvgIconModule, SpinnerComponent, ReadOnlyDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonComponent implements OnInit {
  @Input() aria: { atomic?: string; controls?: string; describedby?: string; expanded?: boolean; label?: string };
  @Input() autofocus?: boolean;
  @Input() dataTracking?: string;
  @Input() dataCy?: string;
  @Input() disabled: boolean;
  @Input() ignoreReadOnly: boolean = false;
  @Input() isLoading?: boolean;
  @Input() icon: GlobalIcon | string;
  @Input() iconSize: GlobalIconSize | number = GlobalIconSize.Medium;
  @Input() id: string;
  @Input() role: string;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() minWidth?: string;

  @Output() buttonClicked: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  @ViewChild('button', { read: ElementRef, static: true }) buttonRef: ElementRef;

  classes: WritableSignal<{
    editor: boolean;
    primary: boolean;
    loading?: boolean;
    neutral?: boolean;
    dashboard?: boolean;
    secondary?: boolean;
    danger?: boolean;
    small?: boolean;
    borderless?: boolean;
    'icon-included'?: boolean;
    'icon-only'?: boolean;
    'animations-disabled'?: boolean;
  }> = signal({ editor: true, primary: true, 'animations-disabled': true });

  ngOnInit(): void {
    timer(0).subscribe(() => {
      if (this.autofocus) {
        this._focusButton();
      }
      this._setIconRelatedClasses();
      this._enableAnimations();
    });
  }

  private _enableAnimations(): void {
    this.classes.set({ ...this.classes(), 'animations-disabled': false });
  }

  private _focusButton(): void {
    this.buttonRef.nativeElement.focus();
  }

  private _setIconRelatedClasses(): void {
    const text = this.buttonRef.nativeElement.innerText.trim();
    this.classes.set({
      ...this.classes(),
      'icon-included': !!text && !!this.icon,
      'icon-only': !text && !!this.icon,
      loading: this.isLoading,
    });
  }
}
